'use client';

import { Rows3 } from 'lucide-react';
import {
  FAIRTRADE_PAGE_SIZES,
  type FairtradePageSize,
} from '@/lib/fairtrade-query';
import { cn } from '@/lib/utils';

type FairtradePageSizeSelectProps = {
  pageSize: FairtradePageSize;
  onPageSizeChange: (size: FairtradePageSize) => void;
  isDark?: boolean;
};

/** Seletor de quantidade de powers por página (topo da lista). */
export default function FairtradePageSizeSelect({
  pageSize,
  onPageSizeChange,
  isDark = true,
}: FairtradePageSizeSelectProps) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-2xl border px-2.5 py-1.5',
        isDark
          ? 'border-sky-400/25 bg-gradient-to-r from-sky-500/10 to-transparent shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
          : 'border-sky-200/90 bg-gradient-to-r from-sky-50 to-white shadow-sm'
      )}
    >
      <span
        className={cn(
          'hidden items-center gap-1.5 font-poppins text-[10px] font-semibold uppercase tracking-wider sm:inline-flex',
          isDark ? '!text-sky-200/80' : 'text-sky-700/80'
        )}
      >
        <Rows3 className="h-3.5 w-3.5 shrink-0 !text-sky-200/80" aria-hidden />
        Exibir
      </span>
      <div
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full p-0.5',
          isDark ? 'bg-[#01012e]/80' : 'bg-white/90 ring-1 ring-sky-100'
        )}
        role="group"
        aria-label="Powers por página"
      >
        {FAIRTRADE_PAGE_SIZES.map((size) => {
          const active = pageSize === size;
          return (
            <button
              key={size}
              type="button"
              onClick={() => onPageSizeChange(size)}
              className={cn(
                'inline-flex h-8 min-w-10 items-center justify-center rounded-full px-2.5',
                'font-poppins text-xs font-bold tabular-nums transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
                active
                  ? isDark
                    ? 'bg-sky-400/25 !text-sky-50 shadow-sm ring-1 ring-sky-400/40'
                    : 'bg-sky-600 text-white shadow-sm'
                  : isDark
                    ? '!text-blue-200/70 hover:bg-white/[0.04] hover:!text-sky-100'
                    : 'text-slate-500 hover:bg-sky-50 hover:text-sky-800'
              )}
              aria-pressed={active}
            >
              {size}
            </button>
          );
        })}
      </div>
    </div>
  );
}
